import { Controller } from "@hotwired/stimulus"

// Map slider selectors to the Swiper modules they need
const modulesMap = {
  "location-slider-one": ["Navigation"],
  "testimonial-slider-one": ["Navigation", "EffectFade", "Autoplay"],
  "testimonial-slider-two": ["Navigation", "Autoplay"],
  "category-slider-one": ["Navigation"],
  "hospital-slider-two": ["Navigation"],
  "doctor-slider-one": ["Navigation"],
  "brand-slider": ["Autoplay"],
  "hospital-Image-slider": ["Navigation"]
}

// Slider configurations
const sliderConfigs = [
  {
    selector: ".location-slider-one",
    options: {
      loop: true,
      speed: 1000,
      spaceBetween: 24,
      navigation: { nextEl: ".location-next", prevEl: ".location-prev" },
      breakpoints: {
        0: { slidesPerView: 1 },
        768: { slidesPerView: 1.5 },
        992: { slidesPerView: 2.2 },
        1200: { slidesPerView: 2.3 },
        1300: { slidesPerView: 2.5 },
        1400: { slidesPerView: 2.9 },
        1600: { slidesPerView: 3.3, spaceBetween: 30 }
      }
    }
  },
  {
    selector: ".testimonial-slider-one",
    options: {
      loop: false,
      speed: 1000,
      slidesPerView: 1,
      autoplay: { delay: 3500, disableOnInteraction: true },
      navigation: { nextEl: ".testimonial-next", prevEl: ".testimonial-prev" }
    }
  },
  {
    selector: ".testimonial-slider-two",
    options: {
      loop: true,
      speed: 1000,
      slidesPerView: 1,
      spaceBetween: 24,
      autoplay: { delay: 3500, disableOnInteraction: true },
      navigation: { nextEl: ".testimonial-next", prevEl: ".testimonial-prev" },
      breakpoints: {
        0: { slidesPerView: 1 },
        992: { slidesPerView: 1.3 },
        1200: { slidesPerView: 1.6 },
        1400: { slidesPerView: 2 }
      }
    }
  },
  {
    selector: ".category-slider-one",
    options: {
      loop: true,
      speed: 1000,
      slidesPerView: 1,
      spaceBetween: 24,
      navigation: { nextEl: ".category-next", prevEl: ".category-prev" },
      breakpoints: {
        0: { slidesPerView: 1 },
        768: { slidesPerView: 3 },
        1200: { slidesPerView: 4 },
        1400: { slidesPerView: 5, spaceBetween: 23 },
        1600: { slidesPerView: 6, spaceBetween: 28 }
      }
    }
  },
  {
    selector: ".hospital-slider-two",
    options: {
      loop: false,
      speed: 1000,
      slidesPerView: 1,
      spaceBetween: 24,
      navigation: { nextEl: ".hospital-next", prevEl: ".hospital-prev" },
      breakpoints: {
        0: { slidesPerView: 1 },
        768: { slidesPerView: 2 },
        1200: { slidesPerView: 3 },
        1400: { slidesPerView: 3, spaceBetween: 23 },
        1600: { slidesPerView: 4, spaceBetween: 30 }
      }
    }
  },
  {
    selector: ".doctor-slider-one",
    options: {
      loop: true,
      speed: 1000,
      slidesPerView: 1,
      spaceBetween: 24,
      navigation: { nextEl: ".doctor-next", prevEl: ".doctor-prev" },
      breakpoints: {
        0: { slidesPerView: 1 },
        768: { slidesPerView: 2 },
        1200: { slidesPerView: 3 },
        1400: { slidesPerView: 3, spaceBetween: 23 },
        1600: { slidesPerView: 4, spaceBetween: 30 }
      }
    }
  },
  {
    selector: ".brand-slider",
    options: {
      loop: false,
      speed: 8000,
      spaceBetween: 45,
      simulateTouch: false,
      autoplay: { delay: 1, disableOnInteraction: false },
      breakpoints: {
        0: { slidesPerView: 1.8 },
        576: { slidesPerView: 2.8 },
        768: { slidesPerView: 3.5 },
        992: { slidesPerView: 4.5 },
        1200: { slidesPerView: 5.5 },
        1400: { slidesPerView: 6 }
      }
    }
  },
  {
    selector: ".hospital-Image-slider",
    options: {
      loop: true,
      speed: 1000,
      slidesPerView: 1,
      spaceBetween: 24,
      navigation: { nextEl: ".hospital-next", prevEl: ".hospital-prev" }
    }
  }
]

export default class extends Controller {
  static initialized = false

  connect() {
    if (this.hasRunAlready()) return
    this.initLazyObservers()
    document.addEventListener("turbo:render", () => this.initLazyObservers())
  }

  hasRunAlready() {
    if (this.constructor.initialized) return true
    this.constructor.initialized = true
    return false
  }

  initLazyObservers() {
    const activeConfigs = sliderConfigs
      .flatMap(cfg => {
        const elements = document.querySelectorAll(cfg.selector)
        return Array.from(elements).map(el => ({ el, config: cfg.options }))
      })
      .filter(Boolean)

    if (!activeConfigs.length) return

    // IntersectionObserver callback
const observer = new IntersectionObserver((entries, obs) => {
  entries.forEach(async entry => {
    if (!entry.isIntersecting) return
    const el = entry.target
    const className = Array.from(el.classList).find(c => modulesMap[c]) || el.classList[0]
    const modulesNeeded = modulesMap[className] || []

    // Dynamically import Swiper core and modules only once
    const importedModule = await import("../vendors/slider.js");
    const SwiperCore = importedModule.default || importedModule;
    const modulesToUse = modulesNeeded.map(mod => importedModule[mod]).filter(Boolean);
    SwiperCore.use(modulesToUse);

    if (!el.swiper) {
      const config = JSON.parse(el.dataset.swiperConfig);
      const slideCount = el.querySelectorAll('.swiper-slide').length;

      // Disable loop if not enough slides
      if (config.loop) {
        let slidesPerView = config.slidesPerView || 1;
        if (config.breakpoints) {
          const windowWidth = window.innerWidth;
          const breakpointKeys = Object.keys(config.breakpoints).map(Number).sort((a, b) => b - a);
          const activeBreakpoint = breakpointKeys.find(key => windowWidth >= key);
          if (activeBreakpoint !== undefined && config.breakpoints[activeBreakpoint].slidesPerView) {
            slidesPerView = config.breakpoints[activeBreakpoint].slidesPerView;
          }
        }
        if (slideCount <= slidesPerView) {
          config.loop = false;
        }
      }

      el.swiper = new SwiperCore(el, config);
    }
    obs.unobserve(el);
  })
}, { rootMargin: "200px" })


    activeConfigs.forEach(cfg => {
      cfg.el.dataset.swiperConfig = JSON.stringify(cfg.config)
      observer.observe(cfg.el)
    })
  }
}
